// REMIX HMR BEGIN
import * as __hmr__ from "remix:hmr";
if (import.meta) {
import.meta.hot = __hmr__.createHotContext(
//@ts-expect-error
"app/hooks/useUserMetadata.ts"
);
import.meta.hot.lastModified = "1748318345465.103";
}
// REMIX HMR END

import { useQuery } from 'react-query'
import { useRoomContext } from './useRoomContext'

interface UserMetadata {
	displayName: string
	firstName?: string
	lastName?: string
	timeZone?: string
	photob64?: string
	photoUrl?: string
}

export function useUserMetadata(email: string) {
	const { userDirectoryUrl, currentUser } = useRoomContext()

	const search = new URLSearchParams({ email })
	const key = `${userDirectoryUrl}?${search}`

	const initialData: UserMetadata = {
		displayName: email,
	}

	// Check if this user is the current authenticated user
	// Handle various email formats and comparisons
	const normalizedEmail = email.toLowerCase().trim()
	const isCurrentUser = currentUser && (
		currentUser.email?.toLowerCase() === normalizedEmail || 
		currentUser.displayName?.toLowerCase() === normalizedEmail ||
		currentUser.email?.split('@')[0]?.toLowerCase() === normalizedEmail ||
		// Also check if the provided email is just the username part of current user's email
		(normalizedEmail.includes('@') && currentUser.email?.toLowerCase() === normalizedEmail) ||
		(!normalizedEmail.includes('@') && currentUser.email?.toLowerCase().startsWith(normalizedEmail + '@'))
	)

	// Use the current user's photo if this is the authenticated user
	if (isCurrentUser && currentUser?.photos?.[0]?.value) {
		initialData.photoUrl = currentUser.photos[0].value
		initialData.displayName = currentUser.displayName
	}
	// Don't set a fallback URL here - let the component handle it
	// This prevents unnecessary requests to external services


	return useQuery({
		initialData,
		queryKey: [key, currentUser?.email],
		queryFn: async ({ queryKey: _queryKey }) => {
			// Always ensure the photoUrl is available
			let result = { ...initialData }

			// If this is the current user, we already have their data
			if (isCurrentUser) {
				return result
			}

			if (userDirectoryUrl === undefined) return Promise.resolve(result)

			try {
				const response = await fetch(key, { credentials: 'include' })

				if (
					response.headers.get('Content-Type')?.startsWith('application/json')
				) {
					const parsedData: UserMetadata = (await response.json()) as any

					// If we got data from the directory, use it
					result = {
						...parsedData,
						displayName: `${parsedData.firstName} ${parsedData.lastName}`,
						// Keep the photoUrl from initialData if it exists (user's actual photo)
						photoUrl: initialData.photoUrl || parsedData.photoUrl,
					}
				}
			} catch (error) {
				console.error('Error fetching user metadata:', error)
			}

			// Always ensure the photo URL is returned
			return result
		},
	})
}
